// Layout Stage A (docs/layout-redesign.md, v128.51) — the 🏠 Today screen.
// The staff app's LANDING tab: recognition first (Matt: "people should feel
// recognised"), then the day's routine as ONE deep-linking list, quick links,
// and the condensed target + brief. Backed by GET /api/today + GET /api/search
// (src/worker/routes/today.js) — both tolerant, so a broken section renders
// as a quiet dash, never a broken screen.
//
// Classic-script global contract: esbuild globalName clobbers window.Today,
// so every onclick-reachable function MUST be in the export list at the
// bottom (the chat bundle lesson).

/* global DB, TARGETS, Chat, currentStore, currentWeekStart, switchScreen, escapeHtml */

var _data = null;          // last /api/today payload
var _kudos = null;         // v129.45 — the 👏 wall + sends-left (own fetch, own card)
var _training = null;      // v132.2 — this week's training for the row chip (own tolerant fetch)
var _loading = false;
var _searchTimer = null;
var _lastQ = '';
// v136.8 (the 16 Oct render audit): the screen used to rebuild the WHOLE of
// #todayRoot, search box included, on the first draw and again as each of its
// three reads landed, so a word typed into search in the first second was
// wiped. Now the search box is drawn once and kept; only the part below it is
// rebuilt, and only when its markup actually changed (the signature skip, as
// in the chat poll and the admin nav). The three reads run together and draw
// once when all have answered, and a revisit inside a minute reuses them.
var _bodyHtml = null;      // the markup last written into #tdBody
var _fetchedAt = 0;        // when the three reads last started (0 = never, or the last try failed)
var _fetchedFor = null;    // the store they were for
var REFETCH_MS = 60000;    // a revisit inside this reuses what is on screen

function api(path, opts) {
  opts = opts || {};
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  if (tk) headers['Authorization'] = 'Bearer ' + tk;
  return fetch(base + path, { method: opts.method || 'GET', headers: headers, body: opts.body, cache: 'no-store' });
}

function myStore() {
  try { return (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : null; } catch (e) { return null; }
}

function esc(s) {
  if (typeof escapeHtml === 'function') return escapeHtml(String(s == null ? '' : s));
  return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}

// Badge names come from the worker catalogue via the achievements shelf; we
// only need a friendly label for the strip. Keep it emoji-first and generic —
// the shelf is one tap away for detail.
var BADGE_LABELS = {
  'compliance-champions': '✅ Compliance Champions',
  star: '⭐ Star award',
};
function badgeLabel(key) { return BADGE_LABELS[key] || ('🏆 ' + String(key || '').replace(/-/g, ' ')); }

// The notice-board quick links (mirrors the news bundle's LINKS — that array
// is module-internal there; Stage B unifies them into the Links tab).
var QUICK_LINKS = [
  { label: '🎓 EnterTrainer', url: 'https://theentertrainer.learningpool.com/login/index.php' },
  { label: '🧸 elctoys.com', url: 'https://elctoys.com/' },
  { label: '🎫 Tazkera', url: 'https://tickets.algt-me.com/' },
  { label: '🤖 Ask Jack', url: 'https://elcapp-jack.netlify.app/' },
];

// ── deep links (one tap from a routine row into the real surface) ────────────
function go(where) {
  try {
    // v128.53 (Stage B): 'email'/'chat'/'news'/'tasks'/'links' are real nav
    // destinations — switchScreen pre-picks the Comms view for email itself.
    switchScreen(where);
  } catch (e) { /* nav must never throw */ }
}

function openLink(i) {
  var l = QUICK_LINKS[i];
  if (l) window.open(l.url, '_blank', 'noopener');
}

// ── rendering ────────────────────────────────────────────────────────────────
function chipHtml(state) {
  // state: {n, good} — a count chip; green when 0 outstanding, amber otherwise
  if (state == null) return '<span class="td-chip td-chip-dim">–</span>';
  var good = state.good;
  return '<span class="td-chip ' + (good ? 'td-chip-ok' : 'td-chip-due') + '">' + esc(state.text) + '</span>';
}

function rowHtml(emoji, label, chip, target) {
  return '<button class="td-row" onclick="Today.go(\'' + target + '\')">' +
    '<span class="td-row-emoji">' + emoji + '</span>' +
    '<span class="td-row-label">' + esc(label) + '</span>' +
    chip +
    '<span class="td-row-arrow">›</span>' +
  '</button>';
}

function recognitionHtml(rec) {
  if (!rec) return '';
  var bits = [];
  (rec.staff_wins || []).slice(0, 4).forEach(function (w) {
    bits.push('<div class="td-win">🎉 <b translate="no">' + esc(w.name) + '</b> — ' + esc(badgeLabel(w.badge_key)) + '</div>');
  });
  (rec.store_wins || []).slice(0, 2).forEach(function (w) {
    bits.push('<div class="td-win">🏆 <b>Our store</b> — ' + esc(badgeLabel(w.badge_key)) + (w.period ? ' <span class="td-win-period">(' + esc(w.period) + ')</span>' : '') + '</div>');
  });
  if (!bits.length) {
    return '<div class="td-recog td-recog-quiet">🌱 No new wins this week yet — today\'s a good day to change that.</div>';
  }
  return '<div class="td-recog"><div class="td-recog-title">This week\'s wins</div>' + bits.join('') + '</div>';
}

// 🔥 the store checklist streak (v129.46 — social layer #3). One quiet line,
// only when there IS a streak (under 3 days is routine, and the worker sends
// no line for it). `froze` shows the Duolingo moment: the streak survived a
// missed day, and saying so is what makes the forgiveness feel like a save
// rather than a rounding error.
function streakHtml(st) {
  if (!st || !st.line) return '';
  // v129.50 — the designed flame replaces the leading 🔥 when the Artwork
  // bundle is loaded; the emoji stays in the string as the fallback.
  var line = esc(st.line);
  try {
    if (typeof Artwork !== 'undefined' && Artwork.flameSvg && line.indexOf('🔥') === 0) {
      line = Artwork.flameSvg(18) + line.slice(2);
    }
  } catch (e) { /* emoji fallback */ }
  return '<div class="td-target" style="background:var(--comms-orange-bg);border:1px solid var(--comms-orange-line);color:var(--comms-orange-deep)">' + line +
    (st.froze ? ' <span style="opacity:.75;font-size:11px">(one missed day forgiven 🧊)</span>' : '') + '</div>';
}

function targetHtml() {
  var st = myStore();
  try {
    if (st && typeof TARGETS !== 'undefined' && TARGETS.getTargets) {
      // Dubai is UTC+4 — todayStr() is the localDateStr helper, never toISOString
      var todayKey = (typeof todayStr === 'function') ? todayStr() : null;
      if (!todayKey) return '';
      // v136.8: getTargets answers by DAY NAME for the week that starts on
      // weekStart, so looking up today's DATE never matched and this card never
      // showed. Ask for the week that holds today (not whichever week the rota
      // is showing), then take today's day name. 0 means no target loaded.
      var d = new Date(todayKey + 'T00:00:00');
      var wk = (typeof getWeekStart === 'function') ? getWeekStart(d) : (typeof currentWeekStart !== 'undefined' ? currentWeekStart : null);
      if (!wk) return '';
      var t = TARGETS.getTargets(st, wk);
      var dayName = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
      var v = t ? Number(t[dayName]) : 0;
      if (v > 0) return '<div class="td-target">🎯 Today\'s target: <b>' + esc(v.toLocaleString()) + '</b></div>';
    }
  } catch (e) { /* target card is optional */ }
  return '';
}

function briefHtml(brief) {
  if (!brief || !brief.posted || !brief.preview) return '';
  return '<div class="td-brief" id="tdBrief" onclick="Today.expandBrief()">' +
    '<div class="td-brief-hd">📋 Today\'s brief</div>' +
    '<div class="td-brief-body td-clamp" id="tdBriefBody" translate="no">' + esc(brief.preview) + '</div>' +
  '</div>';
}

function expandBrief() {
  var el = document.getElementById('tdBriefBody');
  if (el) el.classList.toggle('td-clamp');
}

// ── 👏 Kudos (v129.45 — social layer #1) ────────────────────────────────────
// The Strava mechanic: a one-tap shout-out from shop to shop. The wall shows
// what YOUR store received (the trophy shelf — the chat drop is the doorbell);
// the send box is right under it because seeing kudos is what prompts sending
// one. Capped 3/day server-side; the counter here is a courtesy.
function kudosWhen(iso) {
  try {
    var mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (!(mins >= 0)) return '';
    if (mins < 60) return 'today';
    var days = Math.floor(mins / 1440);
    if (days < 1) return 'today';
    if (days === 1) return 'yesterday';
    return days + ' days ago';
  } catch (e) { return ''; }
}

function kudosHtml() {
  var k = _kudos;
  var wall = (k && k.wall) || [];
  var rows = wall.slice(0, 5).map(function (w) {
    // v135.1: the store's name and the note someone wrote are never translated.
    return '<div class="td-win">👏 <b translate="no">' + esc(w.from) + '</b>' +
      (w.note ? ' — <span translate="no">"' + esc(w.note) + '"</span>' : '') +
      ' <span class="td-win-period">' + esc(kudosWhen(w.at)) + '</span></div>';
  }).join('');
  // The picker: every other active store, off the ALL_STORES global (the
  // classic-script contract — loaded by /api/stores at boot).
  var st = myStore();
  var stores = [];
  try {
    if (typeof ALL_STORES !== 'undefined' && Array.isArray(ALL_STORES)) {
      stores = ALL_STORES.filter(function (s) { return s && s.name && s.name !== st && s.active !== false; })
        .map(function (s) { return s.name; }).sort();
    }
  } catch (e) { /* picker renders empty — send still possible via chat */ }
  var left = k ? k.sends_left : null;
  var sendBox = (left !== null && stores.length)
    ? (left > 0
      ? '<div style="display:flex;gap:6px;margin-top:8px;flex-wrap:wrap">' +
        '<select id="tdKudosStore" style="flex:1;min-width:150px;padding:8px;border:1.5px solid var(--border,#dde3e8);border-radius:9px;font-family:inherit;font-size:13px">' +
          '<option value="">👏 Give kudos to…</option>' +
          stores.map(function (n) { return '<option>' + esc(n) + '</option>'; }).join('') +
        '</select>' +
        '<input id="tdKudosNote" maxlength="140" placeholder="Why? (optional)" style="flex:2;min-width:140px;padding:8px;border:1.5px solid var(--border,#dde3e8);border-radius:9px;font-family:inherit;font-size:13px">' +
        '<button onclick="Today.sendKudos()" style="border:none;background:var(--elc-green,#009E3D);color:#fff;font-weight:800;font-size:13px;padding:8px 14px;border-radius:9px;cursor:pointer">Send</button>' +
        '<div style="flex-basis:100%;font-size:11px;color:var(--comms-hint)">' + left + ' of ' + (k.cap || 3) + ' left today — they are scarce on purpose.</div>' +
        '</div>'
      // Out of sends: say so instead of a button that 429s.
      : '<div style="font-size:11.5px;color:var(--comms-hint);margin-top:6px">All ' + (k.cap || 3) + ' kudos used for today — they reset at midnight.</div>')
    : '';
  if (!rows && left === null) return '';   // not loaded / signed out — no card
  var clap = '👏';
  try { if (typeof Artwork !== 'undefined' && Artwork.clapSvg) clap = Artwork.clapSvg(18); } catch (e) { /* emoji fallback */ }
  return '<div class="td-card"><div class="td-card-title">' + clap + ' Kudos</div>' +
    (rows || '<div style="font-size:12.5px;color:var(--comms-hint)">No kudos yet — be the store that sends the first one.</div>') +
    sendBox + '</div>';
}

// v132.2 — weekly training. Own tolerant fetch, like kudos: a failure just
// leaves the chip blank. Identified by the shared who-record when there is
// one (so the chip can say "done" for THIS person), else by the store.
// v136.8: `quiet` = do not draw (renderTodayScreen draws once after all three
// reads); each returns false only when the read failed, so the next visit retries.
async function loadTraining(quiet) {
  var st = myStore();
  if (!st) return true;
  var who = null;
  try { var raw = localStorage.getItem('chat_who_v1'); if (raw) { var w = JSON.parse(raw); if (w && w.id) who = w; } } catch (e) { /* none */ }
  try {
    var q = who ? 'staff_id=' + encodeURIComponent(who.id) : 'store=' + encodeURIComponent(st);
    var res = await api('/api/training/current?' + q);
    if (!res.ok) return false;
    var d = await res.json().catch(function () { return null; });
    if (d && d.ok) { _training = { session: d.session || null, who: !!who }; if (quiet !== true) render(); }
    return true;
  } catch (e) { return false; /* chip just stays absent */ }
}

async function loadKudos(quiet) {
  var st = myStore();
  if (!st) return true;
  try {
    var res = await api('/api/kudos?store=' + encodeURIComponent(st));
    if (!res.ok) return false;
    var d = await res.json().catch(function () { return null; });
    if (d && d.ok) { _kudos = d; if (quiet !== true) render(); }
    return true;
  } catch (e) { return false; /* card just stays absent */ }
}

async function sendKudos() {
  var sel = document.getElementById('tdKudosStore');
  var note = document.getElementById('tdKudosNote');
  var to = sel && sel.value;
  if (!to) { if (typeof showToast === 'function') showToast('Pick a store to send kudos to'); return; }
  try {
    // v131.39: the Worker needs a PIN (the sender's store is the staff row's) or a Bearer.
    var kAuth = (typeof DB !== 'undefined' && DB.authFieldsOrNull) ? DB.authFieldsOrNull() : null;
    var res = await api('/api/kudos?store=' + encodeURIComponent(myStore() || ''), {
      method: 'POST',
      body: JSON.stringify(Object.assign({ to_store: to, note: (note && note.value) || '', store: myStore() }, kAuth || {})),
    });
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || !d.ok) { if (typeof showToast === 'function') showToast((d && d.error) || 'Could not send the kudos', 'error'); return; }
    if (typeof showToast === 'function') showToast('👏 Kudos sent to ' + to + '!', 'success');
    // v136.8: a redraw now keeps a half-written kudos (see render), so clear the
    // one just sent, as the old full rebuild did.
    if (sel) sel.value = '';
    if (note) note.value = '';
    loadKudos();   // refresh sends-left
  } catch (e) {
    if (typeof showToast === 'function') showToast('Could not send — check the connection', 'error');
  }
}

function render() {
  var root = document.getElementById('todayRoot');
  if (!root) return;
  var st = myStore();
  if (!st) { _bodyHtml = null; root.innerHTML = '<div class="td-empty">Sign in to see your day.</div>'; return; }

  var d = _data;
  var news = d && d.news, email = d && d.email, cl = d && d.checklist, tasks = d && d.tasks, brief = d && d.brief;

  var rows =
    rowHtml('📰', 'Confirm the news',
      chipHtml(news ? { text: news.pending > 0 ? String(news.pending) + ' to confirm' : 'all confirmed', good: !news.pending } : null), 'news') +
    rowHtml('📋', 'Daily brief',
      chipHtml(brief ? { text: brief.posted ? 'posted' : 'not posted yet', good: brief.posted } : null), 'news') +
    rowHtml('📧', 'Store email',
      chipHtml(email ? { text: email.unread > 0 ? String(email.unread) + ' unread' : 'inbox clear', good: !email.unread } : null), 'email') +
    rowHtml('✅', 'Checklists',
      chipHtml(cl ? { text: String(cl.done) + '/' + String(cl.total) + ' done', good: cl.total > 0 && cl.done >= cl.total } : null), 'checklist') +
    rowHtml('💬', 'Chat', '<span class="td-chip td-chip-dim" id="tdChatChip">open</span>', 'chat') +
    rowHtml('🗒️', 'Today\'s tasks',
      chipHtml(tasks ? { text: tasks.open > 0 ? String(tasks.open) + ' open' : 'all done', good: !tasks.open } : null), 'tasks') +
    // v132.2 — weekly training. No session -> no chip (not "all done"); with a
    // who-record the chip is personal, otherwise it just says the week is live.
    rowHtml('🎓', 'Weekly training',
      chipHtml(_training && _training.session
        ? (_training.who
          ? { text: _training.session.completed ? 'done' : 'to do', good: !!_training.session.completed }
          : { text: 'this week', good: false })
        : null), 'training');

  var links = QUICK_LINKS.map(function (l, i) {
    return '<button class="td-link" onclick="Today.openLink(' + i + ')">' + esc(l.label) + '</button>';
  }).join('');

  var bodyHtml =
    (_loading && !d ? '<div class="td-empty">Loading your day…</div>' : '') +
    recognitionHtml(d && d.recognition) +
    streakHtml(d && d.streak) +
    kudosHtml() +
    '<div class="td-card"><div class="td-card-title">Your day</div>' + rows + '</div>' +
    '<div class="td-links"><button class="td-link" onclick="Today.go(\'policies\')">📖 Policies</button>' + links + '</div>' +
    targetHtml() +
    briefHtml(brief);

  // v136.8: the search box is drawn ONCE and never rebuilt by a redraw, so what
  // somebody is typing (and the results under it) survives the reads landing.
  var body = document.getElementById('tdBody');
  if (!body || !root.contains(body) || !document.getElementById('tdSearch')) {
    root.innerHTML =
      '<div class="td-search-wrap">' +
        '<input class="td-search" id="tdSearch" placeholder="🔍 Search news, tasks, chat, email, policies…" ' +
          'oninput="Today.onSearchInput()" autocomplete="off">' +
        '<div class="td-search-results" id="tdSearchResults" style="display:none"></div>' +
      '</div>' +
      '<div id="tdBody"></div>';
    body = document.getElementById('tdBody');
    _bodyHtml = null;
  }
  // The signature skip: unchanged markup is not rebuilt (no flicker, no lost tap).
  if (bodyHtml !== _bodyHtml) {
    var draft = kudosDraft();
    body.innerHTML = bodyHtml;
    _bodyHtml = bodyHtml;
    restoreKudosDraft(draft);
  }

  // chat chip rides the live client badge (chat unread is per-device state)
  try {
    var pill = document.getElementById('chatNavBadge');
    var chip = document.getElementById('tdChatChip');
    if (pill && chip && pill.textContent) { chip.textContent = pill.textContent + ' new'; chip.className = 'td-chip td-chip-due'; }
    // v136.8: the chip is no longer rebuilt on every draw, so put it back when the unread count clears.
    else if (chip && chip.className !== 'td-chip td-chip-dim') { chip.textContent = 'open'; chip.className = 'td-chip td-chip-dim'; }
  } catch (e) { /* cosmetic */ }
}

async function refresh(quiet) {
  var st = myStore();
  if (!st) { render(); return true; }
  _loading = true;
  var ok = false;
  try {
    var res = await api('/api/today?store=' + encodeURIComponent(st));
    if (res && res.ok) {
      var j = await res.json();
      if (j && j.ok) { _data = j; ok = true; }
    }
  } catch (e) { /* keep the last good payload */ }
  _loading = false;
  if (quiet !== true) render();
  return ok;
}

// The kudos picker and note are in the rebuilt part: keep a half-written one.
function kudosDraft() {
  var sel = document.getElementById('tdKudosStore');
  var note = document.getElementById('tdKudosNote');
  return { to: sel ? sel.value : '', note: note ? note.value : '' };
}
function restoreKudosDraft(dr) {
  if (!dr || (!dr.to && !dr.note)) return;
  var sel = document.getElementById('tdKudosStore');
  var note = document.getElementById('tdKudosNote');
  if (sel && dr.to) sel.value = dr.to;
  if (note && dr.note) note.value = dr.note;
}

// entry the nav ladder calls — paint immediately from cache, refresh behind.
// v136.8: `force` (sign-in, and the return-to-app refresh) always reads again;
// a plain revisit inside REFETCH_MS of the last reads, for the same store,
// just draws what it has. The three reads run together and draw ONCE.
function renderTodayScreen(force) {
  var st = myStore();
  var stale = force === true || !st || _fetchedFor !== st || (Date.now() - _fetchedAt) >= REFETCH_MS;
  if (!stale) { render(); return Promise.resolve(); }
  _fetchedAt = Date.now();
  _fetchedFor = st;
  _loading = true;   // the first draw (nothing cached yet) says "Loading your day..."
  render();
  return Promise.all([
    refresh(true),
    loadKudos(true),   // v129.45 — the 👏 wall rides its own tolerant fetch
    loadTraining(true),   // v132.2 — the 🎓 chip rides its own too
  ]).then(function (oks) {
    _loading = false;
    if (oks.indexOf(false) !== -1) _fetchedAt = 0;   // a failed read is tried again on the next visit
    render();
  });
}

// ── super-search ─────────────────────────────────────────────────────────────
function onSearchInput() {
  var el = document.getElementById('tdSearch');
  var q = el ? el.value.trim() : '';
  if (_searchTimer) clearTimeout(_searchTimer);
  if (q.length < 2) { hideResults(); return; }
  // tabs answer immediately; the content fan-out fills in behind
  var box = document.getElementById('tdSearchResults');
  var tabs = tabRowsHtml(q);
  if (box && tabs) { box.innerHTML = tabs; box.style.display = 'block'; }
  _searchTimer = setTimeout(function () { runSearch(q); }, 350);
}

function hideResults() {
  var box = document.getElementById('tdSearchResults');
  if (box) { box.style.display = 'none'; box.innerHTML = ''; }
}

async function runSearch(q) {
  var st = myStore();
  if (!st) return;
  _lastQ = q;
  try {
    var res = await api('/api/search?store=' + encodeURIComponent(st) + '&q=' + encodeURIComponent(q));
    if (!res || !res.ok) return;
    var j = await res.json();
    if (!j || !j.ok || j.q !== undefined && _lastQ !== q) { /* stale */ }
    renderResults(j.results || {});
  } catch (e) { /* search is best-effort */ }
}

// v129.0 (Matt on staging: "search could be better - i would want it to show
// the tabs") — the super-search matches TAB DESTINATIONS first, instantly,
// like a command palette: "em" offers the Email tab before any content lands.
var TAB_DESTS = [
  { label: '📧 Email', go: 'email', words: ['email', 'mail', 'inbox'] },
  { label: '💬 Chat', go: 'chat', words: ['chat', 'messages'] },
  { label: '📰 News', go: 'news', words: ['news', 'updates', 'brief'] },
  { label: '🗒️ Tasks', go: 'tasks', words: ['tasks', 'todo', 'surveys'] },
  { label: '🎓 Training', go: 'training', words: ['training', 'quiz', 'learn', 'policy quiz', 'weekly training'] },   // v132.2
  { label: '✅ Checks', go: 'checklist', words: ['checks', 'checklist', 'opening', 'closing'] },
  { label: '⏰ Clock', go: 'clock', words: ['clock', 'shift', 'clock in', 'clock out'] },
  { label: '📞 Calls', go: 'calls', words: ['calls', 'video', 'ring'] },
  { label: '📅 Rota', go: 'rota', words: ['rota', 'schedule', 'roster'] },
  { label: '📊 KPI', go: 'kpi', words: ['kpi', 'sales', 'performance'] },
  { label: '🌴 Balances', go: 'toil', words: ['balances', 'toil', 'holiday', 'leave'] },
  { label: '⭐ Reviews', go: 'customer', words: ['reviews', 'customer', 'feedback'] },
  { label: '📖 Policies', go: 'policies', words: ['policies', 'policy'] },
  { label: '🔗 Links', go: 'links', words: ['links', 'entertrainer', 'tazkera'] },
];
function tabMatches(q) {
  var s = String(q || '').toLowerCase().trim();
  if (s.length < 2) return [];
  return TAB_DESTS.filter(function (d) {
    if (d.label.toLowerCase().indexOf(s) !== -1) return true;
    return d.words.some(function (w) { return w.indexOf(s) === 0 || s.indexOf(w) === 0; });
  }).slice(0, 4);
}
function tabRowsHtml(q) {
  var tabs = tabMatches(q);
  if (!tabs.length) return '';
  return '<div class="td-sr-group">↪ Go to</div>' + tabs.map(function (d) {
    return '<button class="td-sr-row" style="font-weight:700" onclick="Today.goTab(\'' + d.go + '\')">' + d.label + '</button>';
  }).join('');
}
function goTab(where) { hideResults(); go(where); }

var SECTION_META = [
  { key: 'news', label: '📰 News', target: 'news' },
  { key: 'tasks', label: '🗒️ Tasks', target: 'tasks' },
  { key: 'training', label: '🎓 Training', target: 'training' },   // v132.2
  { key: 'email', label: '📧 Email', target: 'email' },
  { key: 'chat', label: '💬 Chat', target: 'chat' },
  { key: 'policies', label: '📖 Policies', target: 'policies' },
];

function renderResults(results) {
  var box = document.getElementById('tdSearchResults');
  if (!box) return;
  var html = tabRowsHtml(_lastQ);
  SECTION_META.forEach(function (s) {
    var list = results[s.key];
    if (!list || !list.length) return;
    html += '<div class="td-sr-group">' + s.label + '</div>';
    list.forEach(function (r) {
      // v135.1: a result's title is a post, task, email or message someone wrote: never translated.
      html += '<button class="td-sr-row" translate="no" onclick="Today.openResult(\'' + s.key + '\',' + Number(r.id) + ')">' + esc(r.title || '') + '</button>';
    });
  });
  box.innerHTML = html || '<div class="td-sr-none">Nothing found.</div>';   // tabs count as results
  box.style.display = 'block';
}

function openResult(kind, id) {
  hideResults();
  if (kind === 'policies') {
    try { switchScreen('policies'); if (window.Policies && window.Policies.open) window.Policies.open(id); return; } catch (e) { /* fall through */ }
  }
  var meta = null;
  SECTION_META.forEach(function (s) { if (s.key === kind) meta = s; });
  go(meta ? meta.target : 'news');
}

// ── 🔗 the Links tab (Stage B) — the notice-board links as a full screen ─────
function renderLinksScreen() {
  var root = document.getElementById('linksRoot');
  if (!root) return;
  var rows = QUICK_LINKS.map(function (l, i) {
    return '<button class="td-row" onclick="Today.openLink(' + i + ')">' +
      '<span class="td-row-label">' + esc(l.label) + '</span>' +
      '<span class="td-row-arrow">↗</span></button>';
  }).join('');
  root.innerHTML =
    '<div class="td-card"><div class="td-card-title">Store links</div>' +
    '<button class="td-row" onclick="Today.go(\'policies\')">' +
      '<span class="td-row-label">📖 Policies</span><span class="td-row-arrow">›</span></button>' +
    rows + '</div>';
}

export {
  render, renderTodayScreen, refresh, go, openLink, openResult,
  onSearchInput, expandBrief, renderLinksScreen, goTab,
  sendKudos, loadKudos,
};

// window bindings (classic-script contract — the ladder + onclicks use these)
window.renderTodayScreen = renderTodayScreen;
window.renderLinksScreen = renderLinksScreen;
